import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAccountService } from '@/composables/services/useAccountService'
import type { Account } from '@/domain/entities/account.entity'
import { ErrorHandler } from '@/infrastructure/errors'
import { useErrors } from '../composables/app/useErrors'
import { findPhoneCodeByIsoCode } from '@/composables/phone/detect/usePhoneCodeFromCountry'

/**
 * Store для работы с аккаунтами
 * Использует Vue 3 Composition API (setup синтаксис)
 */
export const useAccountStore = defineStore('account', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('account')
  // ========== State ==========
  const account = ref<Account | null>(null)
  const loading = ref(false)

  // ========== Service ==========
  const accountService = useAccountService()

  // ========== Getters (Computed) ==========
  const hasAccount = computed(() => account.value !== null)
  const isLoading = computed(() => loading.value)
  const defaultPhoneCode = computed(() => {
    const iso = account.value?.account_country_iso
    if (!iso) return '380'
    return findPhoneCodeByIsoCode(iso)?.phoneCode ?? '380'
  })

  // ========== Actions ==========

  /**
   * Определить аккаунт по стране (с fallback на UA)
   * @param countryIso - ISO код страны
   * @returns { account, fallbackUsed } или { account: null, fallbackUsed: false } при ошибке
   */
  async function findAccountByCountry(countryIso: string): Promise<{
    account: Account | null
    fallbackUsed: boolean
  }> {
    const empty = { account: null as Account | null, fallbackUsed: false }

    if (!countryIso) {
      return empty
    }

    loading.value = true
    clearErrors()

    try {
      const result = await accountService.detectAccountByCountry(countryIso)

      if (result.account) {
        account.value = result.account
      }

      return {
        account: result.account,
        fallbackUsed: result.fallbackUsed,
      }
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      return empty
    } finally {
      loading.value = false
    }
  }

  /**
   * Завантажити аккаунт по ID (для хардкод-прив'язки)
   */
  async function findAccountById(accountId: string): Promise<Account | null> {
    loading.value = true
    clearErrors()
    try {
      const result = await accountService.getAccountById(accountId)
      if (result) account.value = result
      return result
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Установить текущий аккаунт
   */
  function setAccount(accountData: Account | null) {
    account.value = accountData
  }

  /**
   * Сбросить состояние store
   */
  function reset() {
    account.value = null
    loading.value = false
    clearErrors()
  }

  /**
   * Очистить ошибку
   */
  function clearError() {
    clearErrors()
  }

  // ========== Return ==========
  return {
    // State
    account,
    loading: isLoading,
    error,

    // Getters
    hasAccount,
    hasError,
    defaultPhoneCode,

    // Actions
    findAccountByCountry,
    findAccountById,
    setAccount,
    reset,
    clearError,
  }
})
